import { DialogContextProps } from "@interfaces/System/DialogContextProps";
import { Button, DialogActions, DialogContent } from "@material-ui/core";
import { ModalContentLoading } from "@components/Common/Modal";
import React, { useRef } from "react";
import { staffOrderService } from "@services/Staff";
import { useStoreon } from "storeon/react";
import { TableEvents, TableState } from "@store/table-module";
import { useSnackbar } from "notistack";
import { useAsync } from "@hooks";
import { CreateStaffOrderDto, UpdateStaffOrderDto } from "@dto/StaffOrderDto";
import { StaffOrderForm } from "@components/Forms/StaffOrder";

const StaffOrderDialogContext = ({ onClose, modalProps }: DialogContextProps) => {
    const { itemId } = modalProps;

    const formRef = useRef<HTMLFormElement>();
    const { enqueueSnackbar } = useSnackbar();
    const { dispatch: tableDispatch } = useStoreon<TableState, TableEvents>();

    const { data, loadingStatus } = useAsync(staffOrderService.getOneStaffOrder, {
        functionArgs: [itemId],
        canLoad: !!itemId,
    });

    const invokeSubmit = () => {
        formRef.current!.handleSubmit();
    };

    const handleCreateStaffOrder = async (values) => {
        try {
            const dto = staffOrderService.plainToClass(CreateStaffOrderDto, values);
            await staffOrderService.createStaffOrder(dto);
            enqueueSnackbar("Заказ успешно создан", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при создании заказа", {
                variant: "error",
            });
        }
    };

    const handleUpdateStaffOrder = async (values) => {
        if (!data) return;

        try {
            const dto = staffOrderService.plainToClass(UpdateStaffOrderDto, values);
            await staffOrderService.updateStaffOrder(data.id, dto);
            enqueueSnackbar("Заказ успешно обновлен", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при обновлении заказа", {
                variant: "error",
            });
        }
    };

    return (
        <>
            <DialogContent>
                {loadingStatus === "loading" && <ModalContentLoading />}

                {(loadingStatus === "loaded" || loadingStatus === "inactivity") && (
                    <StaffOrderForm
                        ref={formRef}
                        data={data}
                        onSubmit={data ? handleUpdateStaffOrder : handleCreateStaffOrder}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color="default" onClick={onClose}>
                    Отмена
                </Button>
                <Button color="primary" variant="contained" onClick={invokeSubmit}>
                    Сохранить
                </Button>
            </DialogActions>
        </>
    );
};

export default StaffOrderDialogContext;
